import { Button } from '@repo/studio-design-system-ui-button'
import { Dialog } from '@repo/studio-design-system-ui-dialog'
import { resolveTrialActivationConfirm } from '@repo/studio-hooks-coins/use-trial-activation-confirm-dialog'
import { formatCurrency } from '@repo/studio-utils/utils/currency'
import { ROUTES } from '@repo/studio-utils/routes'
import { Link } from '@repo/react-router-locale/locale-router'
import { useUserQuery } from 'auth/hooks'
import { CurrencyCode } from 'codegen/types'
import { getStudioAiPlanPrices } from 'constants/prices'
import * as React from 'react'
import { Trans, useTranslation } from 'react-i18next'

export function TrialActivationConfirmDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('components')
  const { data: studioPlan } = useUserQuery(undefined, {
    select: data => data.me?.user?.subscriptionAccess.studioAi.subscription?.type,
  })
  const price = formatCurrency(
    Math.round(getStudioAiPlanPrices(studioPlan, { includeVat: true }).yearly * 100),
    CurrencyCode.Eur,
  )

  const closeAsCancel = React.useCallback(() => {
    resolveTrialActivationConfirm(false)
  }, [])

  return (
    <Dialog.Root
      open={open}
      onOpenChange={nextOpen => {
        onOpenChange(nextOpen)
        if (!nextOpen) {
          closeAsCancel()
        }
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>
            {t('studio.components.trial_activation_confirm_dialog.title')}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className="flex flex-col gap-4">
            <p className="studio-typography-14 text-theme_semantic_surface_ui_foreground-color-subtle">
              {t('studio.components.trial_activation_confirm_dialog.body', { price })}
            </p>
            <p className="studio-typography-14 text-theme_semantic_surface_ui_foreground-color-subtle">
              <Trans
                ns="components"
                i18nKey="studio.components.trial_activation_confirm_dialog.notice"
                components={{
                  account: (
                    <Link
                      className="underline"
                      to={ROUTES.ACCOUNT_SETTINGS.SUBSCRIPTIONS}
                      onClick={closeAsCancel}
                    />
                  ),
                }}
              />
            </p>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" size="md" onClick={closeAsCancel}>
            {t('studio.components.trial_activation_confirm_dialog.cancel')}
          </Button>
          <Button
            variant="promotional"
            size="md"
            onClick={() => resolveTrialActivationConfirm(true)}
          >
            {t('studio.components.trial_activation_confirm_dialog.confirm')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
